import { difference, isEqual, union, without } from "lodash";
import { DCEvent } from "@/events/eventsDomain";
import { move } from "@/utils/arrays";

export type ColumnSelectorState<Id extends string> = {
  columns: Id[];
  visibleColumns: Id[];
  defaultColumns: Id[];
};

export type ColumnSelectorUpdate<Id extends string> = (
  f: (state: ColumnSelectorState<Id>) => ColumnSelectorState<Id>,
) => DCEvent;

export const hiddenColumns = <Id extends string>({
  columns,
  visibleColumns,
}: ColumnSelectorState<Id>): Id[] => difference(columns, visibleColumns);

export const isDefaultSelection = <Id extends string>({
  visibleColumns,
  defaultColumns,
}: ColumnSelectorState<Id>): boolean => isEqual(visibleColumns, defaultColumns);

export const setColumnVisibility = <Id extends string>(
  state: ColumnSelectorState<Id>,
  id: Id,
  isVisible: boolean,
): ColumnSelectorState<Id> => ({
  ...state,
  visibleColumns: isVisible ? union(state.visibleColumns, [id]) : without(state.visibleColumns, id),
});

export const setAllColumnsVisibility = <Id extends string>(
  state: ColumnSelectorState<Id>,
  isVisible: boolean,
): ColumnSelectorState<Id> => ({
  ...state,
  visibleColumns: isVisible ? union(state.visibleColumns, state.columns) : [],
});

export const moveColumn = <Id extends string>(
  state: ColumnSelectorState<Id>,
  fromId: Id,
  toId: Id,
): ColumnSelectorState<Id> => ({
  ...state,
  visibleColumns: move(state.visibleColumns, fromId, state.visibleColumns.indexOf(toId)),
});

export const resetColumns = <Id extends string>(
  state: ColumnSelectorState<Id>,
): ColumnSelectorState<Id> => ({ ...state, visibleColumns: state.defaultColumns });
